import { createContext, useContext, type PropsWithChildren } from 'react';
import { useMatchMedia } from './useMatchMedia';

/** Breakpoint widths in px used for device type detection. */
export const DEVICE_TYPE_BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  laptop: 1650,
  desktop: 1920,
  widescreen: undefined,
} as const;

export type DeviceType = keyof typeof DEVICE_TYPE_BREAKPOINTS | 'all';

const TABLET_LANDSCAPE_MAX_WIDTH = 1280;

const DeviceTypeContext = createContext<DeviceType | undefined>(undefined);
DeviceTypeContext.displayName = 'DeviceTypeContext';

type DeviceTypeProviderProps = PropsWithChildren<{
  deviceType?: DeviceType;
}>;

export function DeviceTypeProvider({ children, deviceType: inputDeviceType }: DeviceTypeProviderProps) {
  const detectedDeviceType = useDetectedDeviceType(inputDeviceType == null);
  const deviceType = inputDeviceType ?? detectedDeviceType;

  return <DeviceTypeContext.Provider value={deviceType}>{children}</DeviceTypeContext.Provider>;
}

/** Returns the resolved Device Type using various media query listeners. */
export const useDeviceType = (): DeviceType => {
  // We are in kyoko here so we can't know that a DeviceTypeContext exists above us.
  const contextDeviceType = useContext(DeviceTypeContext);
  const enableLocalMediaQuery = contextDeviceType == null; // if we can't already get the value from a context above us, spin up our own media queries.
  const localMediaQueryDeviceType = useDetectedDeviceType(enableLocalMediaQuery);

  return contextDeviceType ?? localMediaQueryDeviceType;
};

function useDetectedDeviceType(enabled = true): DeviceType {
  const isDesktop = useMatchMedia(`(max-width: ${DEVICE_TYPE_BREAKPOINTS.desktop}px)`, enabled);
  const isLaptop = useMatchMedia(`(max-width: ${DEVICE_TYPE_BREAKPOINTS.laptop}px)`, enabled);
  const isTabletLandscape = useMatchMedia(`(max-width: ${TABLET_LANDSCAPE_MAX_WIDTH}px)`, enabled);
  const isTablet = useMatchMedia(`(max-width: ${DEVICE_TYPE_BREAKPOINTS.tablet}px)`, enabled);
  const isMobile = useMatchMedia(`(max-width: ${DEVICE_TYPE_BREAKPOINTS.mobile}px)`, enabled);
  const isTouch = useMatchMedia('(pointer:coarse)', enabled);
  const isHorizontal = useMatchMedia('(orientation: landscape)', enabled);
  const isUserAgentMobile = navigator.userAgent.toLowerCase().includes('mobile');

  if (!enabled) {
    return 'widescreen';
  }

  if ((isMobile && isUserAgentMobile) || (isTablet && isHorizontal && isUserAgentMobile)) {
    return 'mobile';
  }
  if (isTouch && (isTablet || (isHorizontal && isTabletLandscape))) {
    return 'tablet';
  }
  if (isLaptop) {
    return 'laptop';
  }
  if (isDesktop) {
    return 'desktop';
  }
  return 'widescreen';
}

export const useIsMinWidth = (minWidthPx: number): boolean => {
  return useMatchMedia(`(min-width: ${minWidthPx}px)`);
};
